import * as THREE from 'three';
import { Material, Mesh, Object3D, Vector3 } from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';

import { AssetVisibilityManager } from '../asset-visibility-manager';
import { AssetLoadingStatus, AssetType, ElementObj } from './element-obj';

export class Model extends ElementObj {
  model: any;

  locked: boolean;
  name: string;
  assetId: string;
  lineSegments: THREE.LineSegments[] = [];
  visibilityChangedCallback?: (visible: boolean) => Promise<void>;

  constructor(model: any) {
    super(model.timelineContainerId as string);

    this.model = model;
    this.deletable = false;
    this.locked = model.locked || true;

    this.name = this.model.name || 'Ej namngiven';
    this.assetId = this.model.id || null;
    this.visible = model.visible;
    this.locked = this.model.properties.locked === false ? false : true;
    this.wgs84 = this.model.properties.wgs84 && {
      lat: Number(this.model.properties.wgs84.lat),
      lng: Number(this.model.properties.wgs84.lng),
    };
    this.MAS = Number(this.model.properties.MAS);

    void this.setStateVisibility(this.visible);

    this.freeze = {
      translate: this.locked,
      rotateZ: this.locked,
      rotateXY: this.locked,
      scale: true,
    };

    const localPos = this.calculateLocalPosition();
    const localHeight = this.calculateLocalHeight();
    if (localPos) {
      this.position.x = Number(localPos.x);
      this.position.y = Number(localPos.y);
      this.position.z = localHeight || this.model.properties.height || 0;
    } else {
      this.model.properties.pos && this.position.copy(this.model.properties.pos as Vector3);
    }

    if (this.model.properties.rot) {
      const r = this.model.properties.rot;
      this.rotation.setFromQuaternion(
        new THREE.Quaternion(Number(r.x), Number(r.y), Number(r.z), Number(r.w)),
      );
    }
    if (this.model.properties.scale) {
      this.scale.set(
        Number(this.model.properties.scale.x),
        Number(this.model.properties.scale.y),
        Number(this.model.properties.scale.z),
      );
    }

    // If no wgs84 is defined, it is last saved with the deprecated settings
    // Therefore, calculate wgs84 coords and resave it to store it with the new settings.
    if (!this.wgs84 || !this.MAS) {
      this.recalculateWGS84Coords();
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.save();
    }
  }

  initialLoadingStatus(): AssetLoadingStatus {
    return 'loaded';
  }

  get assetType(): AssetType {
    return 'model';
  }

  dispose() {
    this.children.forEach((child: Object3D | Mesh) => {
      this.remove(child);
      if (child instanceof Mesh) {
        child.geometry.dispose();
        (child.material as Material).dispose();
      }
    });
  }

  /**
   * @description This is called each time the TrasformTool is transforming the element
   */
  onTransformed() {
    this.recalculateWGS84Coords();
  }

  /**
   * @description Calculate the geograhpic coords for the points of the element
   */
  recalculateWGS84Coords() {
    const wgs84 = this.calculateWGS84Coords(new THREE.Vector2(this.position.x, this.position.y));
    wgs84 &&
      (this.wgs84 = {
        lng: wgs84.x,
        lat: wgs84.y,
      });

    this.MAS = this.availableMAS() ? this.calculateMAS() : this.MAS;
  }

  setGeometry(geometry: Object3D) {
    geometry && this.add(geometry);
    AssetVisibilityManager.getInstance().publishAssetVisibilityChanged(this);
  }

  hasGeometry() {
    return this.children.length > 0;
  }

  getSaveData() {
    const quat = new THREE.Quaternion().setFromEuler(this.rotation, true);

    const wgs84 = this.calculateWGS84Coords(new THREE.Vector2(this.position.x, this.position.y));

    return Promise.resolve({
      id: this.assetId || null,
      MAS: this.MAS,
      locked: this.locked,
      rot: { x: quat.x, y: quat.y, z: quat.z, w: quat.w },
      scale: { x: this.scale.x, y: this.scale.y, z: this.scale.z },
      wgs84: {
        lng: wgs84!.x.toString(),
        lat: wgs84!.y.toString(),
      },
      timelineContainerId: this.timelineContainerId,
    });
  }

  /**
   * @description Perfoms actions when model is selected
   */
  onSelect() {
    super.onSelect();

    if (this.lineSegments?.length > 0) {
      this.lineSegments.map((segment: any) => {
        segment.parent?.remove(segment);
        segment.geometry.dispose();
        (segment.material as THREE.LineBasicMaterial).dispose();
      });
    }

    this.lineSegments = [];

    const foundGeometry: {
      parent: Object3D;
      lineGeom: THREE.LineSegments;
    }[] = [];

    const addLineGeometry = (node: any) => {
      if (node.geometry) {
        let geometry = undefined;
        if (node.isLine) {
          geometry = node.geometry.clone();
        } else {
          geometry = new THREE.EdgesGeometry(node.geometry);
        }
        const lineGeom = new THREE.LineSegments(
          geometry,
          new THREE.LineBasicMaterial({
            color: 0x31fe51,
            linewidth: 2,
            transparent: true,
            depthTest: true,
          }),
        );
        lineGeom.name = 'selectionLine';
        foundGeometry.push({ parent: node.parent, lineGeom });
      }
      for (const child of node.children) {
        addLineGeometry(child);
      }
    };

    addLineGeometry(this);

    foundGeometry.map((segment: { parent: Object3D; lineGeom: THREE.LineSegments }) => {
      segment.parent.add(segment.lineGeom);
      this.lineSegments.push(segment.lineGeom);
    });
  }

  /**
   * @description Performs actions when it is unselected
   */
  onDeselect() {
    super.onDeselect();

    if (this.lineSegments && this.lineSegments.length > 0) {
      this.lineSegments.map((segment: any) => {
        segment.parent?.remove(segment);
        segment.geometry.dispose();
        (segment.material as LineMaterial).dispose();
      });
    }
    this.lineSegments = [];
  }

  // To ensure that we use get the id after an asset have been saved
  onSaved(data: any) {
    if (!data) {
      return;
    }
  }

  getNativeAssetData() {
    return {
      uuid: this.uuid,
      name: this.name,
      visible: this.visible,
    };
  }

  // Executed on each animate
  onAnimate(camera: THREE.Camera) {
    if (this.locked) {
      this.freeze.translate = true;
      this.freeze.rotateZ = true;
      this.freeze.rotateXY = true;
    } else {
      this.freeze.translate = false;
      this.freeze.rotateZ = false;
      this.freeze.rotateXY = false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  mouseOver(idx: number) {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  pick(x: number, y: number, camera: THREE.Camera) {}

  setVisibilityChangedCallback(callback: (visible: boolean) => Promise<void>) {
    this.visibilityChangedCallback = callback;
  }

  async onStateVisibilityChanged() {
    await this.visibilityChangedCallback?.(this.getStateVisibility());
  }
}
